import { gql } from "lib/apolloClient";
import {
  behaviourConfigurationFragment,
  behaviourCategoryFragment,
} from "./BehaviourConfigurationFragments";
import { controlParameterFragment } from "RolesAndPermissions/modules/RolesAndPermissionsFragments";

export const getBehaviourFeedCategoriesQuery = gql`
  query getBehaviourFeedCategories(
    $id: ID!
    $filters: BehaviourCategoryAssociatedIncidentsCountFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourCategories {
          ...behaviourCategoryFragment
        }
      }
    }
  }
  ${behaviourCategoryFragment}
`;

export const getCurriculumProgramsQuery = gql`
  query getCurriculumPrograms($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        curriculumPrograms {
          id
          label
          acronym
        }
      }
    }
  }
`;

export const getActionsQuery = gql`
  query getActionsQuery($id: ID!, $filters: BehaviourActionFilters) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourActions(filters: $filters) {
          ...actionDetails
        }
      }
    }
  }
  ${behaviourConfigurationFragment.actionDetailsFragment}
`;

export const getSentimentsQuery = gql`
  query getSentimentsQuery($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourSentiments {
          id
          label
          value
        }
      }
    }
  }
`;

export const getBehaviourSeveritiesQuery = gql`
  query getBehaviourSeverites($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourSeverities {
          id
          level
          name
          color
        }
      }
    }
  }
`;

export const getNomenclatureQuery = gql`
  query getNomenclature($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        globalConstants(
          filter: {
            names: [
              "BEHAVIOUR_MODULE_NOMENCLATURE_MODULE"
              "BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT"
              "BEHAVIOUR_MODULE_NOMENCLATURE_ACTION"
              "BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL"
            ]
          }
        ) {
          id
          name
          value
        }
      }
    }
  }
`;

export const getMultiLevelDraftBehaviourCategoryQuery = gql`
  query getMultiLevelDraftBehaviourCategory(
    $organizationId: ID!
    $input: BehaviourCategoryDraftNodesInput!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        behaviourCategoryDraftNodes(input: $input) {
          id
          rootNodes
          nodes
        }
      }
    }
  }
`;

export const getBehaviourSentimentWiseSeveritiesQuery = gql`
  query getBehaviourSentimentWiseSeverities($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourSentimentWiseSeverities {
          sentiment {
            id
            label
            value
          }
          severities {
            id
            name
            level
            color
            associatedIncidentsCount
          }
        }
      }
    }
  }
`;

export const getACLPermissionEnabledRolesQuery = gql`
  query getEnabledRoles($id: ID!) {
    node(id: $id, type: ACL_CONTROL_PARAMETER) {
      ... on ACLPermission {
        id
        enabledRoles(levels: [ACCOUNT, PROGRAMME]) {
          id
          label
          globalLevel
        }
      }
    }
  }
`;

export const getPermissionsWithEnabledRolesQuery = gql`
  query getPermissionsWithEnabledRoles($ids: [ID!]!) {
    multiNode(ids: $ids, type: ACL_CONTROL_PARAMETER) {
      ... on ACLPermission {
        id
        uid
        type
        enabledRoles(levels: [ACCOUNT, PROGRAMME]) {
          id
          label
          globalLevel
        }
      }
    }
  }
`;

export const getRnPLevelsFeedQuery = gql`
  query getRnPLevelsFeed($id: ID!, $rnpViewFilters: RnpViewFilters!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        rnpView(filters: $rnpViewFilters) {
          id
          ... on RnPRole {
            id
            label
          }
        }
      }
    }
  }
`;

export const getRnPRoleDetailsQuery = gql`
  query getRnPRoleDetails($id: ID!, $rnpViewFilters: RnpViewFilters!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        rnpView(filters: $rnpViewFilters) {
          id
          ... on RnPRole {
            id
            label
            globalLevel
            isSuperAdmin
            isCustomRole
            assignedUserCount
          }
        }
      }
    }
  }
`;

export const getRnPPermissionsFeedQuery = gql`
  query getRnPPermissionsFeed(
    $id: ID!
    $roleId: ID!
    $permissionListFilters: ACLPermissionListFilters!
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        aclPermissionsListView(filters: $permissionListFilters) {
          displaySequence
          globalLevel
          type
          children
          childrenModules
          isChildrenModule
          relatedPermissions {
            id
            label
          }
          ...controlParameterItem
          associatedParent {
            ...controlParameterItem
          }
        }
      }
    }
  }
  ${controlParameterFragment.controlParameter}
`;

export const getOrganizationStaffFeedQuery = gql`
  query getOrganizationStaffFeed(
    $first: Int = 10
    $after: String
    $filters: OrgConnectionFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $roleContextFilters: RnPRoleContextDetailFilterInput
  ) {
    platform {
      organization {
        id
        staff(
          first: $first
          after: $after
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          totalCount
          edges {
            node {
              id
              email
              firstName
              middleName
              lastName
              preferredName
              prefix
              suffix
              pronouns
              type
              profileImage
              displayRole
              designation {
                id
                title
              }
              rnpRoleContextDetail(filters: $roleContextFilters) {
                id
                role {
                  id
                  label
                }
                contextType
                context {
                  id
                  ... on IbPypCurriculumProgram {
                    id
                    label
                  }
                  ... on IbMypCurriculumProgram {
                    id
                    label
                  }
                  ... on UBDCurriculumProgram {
                    id
                    label
                  }
                  ... on IbDpCurriculumProgram {
                    id
                    label
                  }
                  ... on GenericCurriculumProgram {
                    id
                    label
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getBehaviourRolesAssignedUsersFeedQuery = gql`
  query getBehaviourRolesAssignedUsersFeed(
    $roleId: ID!
    $searchText: String
    $rnpRoleContextFilters: RnPRoleContextDetailFilterInput
    $assignedUsersContextFilters: RnPRoleContextDetailFilterInput
    $academicYearId: ID!
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $after: String
    $first: Int
  ) {
    node(id: $roleId, type: RNP_ROLE) {
      ... on RnPRole {
        id
        assignedUsers(
          first: $first
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          searchText: $searchText
          filters: $assignedUsersContextFilters
          after: $after
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              firstName
              middleName
              lastName
              preferredName
              prefix
              suffix
              isArchivedV2(filters: { academicYearId: $academicYearId })
              rnpRoleContextDetail(filters: $rnpRoleContextFilters) {
                context {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
